import { type FC } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CircularProgress,
  Link as MuiLink,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { RefundStatusChip, useRefundsQuery } from "@/partner/entities/refunds";
import { useFilter } from "@/partner/shared/components/Filter";
import { EmptyListCard } from "@/partner/shared/components/EmptyListCard";
import { interpolationFormat } from "@/partner/shared/lib";
import { ErrorMessage } from "@/shared/components";
import { formatAmountObject } from "@/partner/features/products";
import { PagePaths } from "@/partner/shared/constants/routes";
import { BuyerEmailCell } from "@/partner/shared/components/BuyerEmailCell";
import type { RefundsListQueryType } from "@/types/graphql/partner.generated";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";
import { buildRefundsFilter } from "../model/buildRefundsFilter";

type RefundRow = RefundsListQueryType["refunds"]["nodes"][number];

// Ключи локализации типа продукта (products.type.ppv/subscription) — тот же
// маппинг, что использует ProductTypeField, переиспользуем вместо нового.
const PRODUCT_TYPE_KEY: Record<string, string> = {
  PPV: "ppv",
  SUBSCRIPTION: "subscription",
};

export const RefundsList: FC = () => {
  const { t, i18n } = useTranslation();
  const {
    filter,
    onPageChange,
    onRowsPerPageChange,
    params: { page, rowsPerPage },
  } = useFilter();

  const { data, loading, error } = useRefundsQuery({
    // page сбрасывается в 0 самим FilterProvider при любом изменении
    // filter[...] (см. changeParams), отдельного сброса здесь не нужно.
    variables: { page, rowsPerPage, filter: buildRefundsFilter(filter) },
    fetchPolicy: "cache-and-network",
  });

  const rows = data?.refunds?.nodes ?? [];
  const count = data?.refunds?.count ?? 0;

  const emptyValue = t("common.emptyDash");

  const formatAmount = (row: RefundRow): string =>
    formatAmountObject(row.amount);

  const formatOrganizationAmount = (row: RefundRow): string =>
    formatAmountObject(
      row.organizationAmountInOrganizationCurrency,
      emptyValue
    );

  const formatProvider = (row: RefundRow): string =>
    row.payment
      ? t(PAYMENT_PROVIDER_LABEL_KEY[row.payment.provider])
      : emptyValue;

  const formatType = (row: RefundRow): string =>
    row.payment?.product?.type
      ? t(`products.type.${PRODUCT_TYPE_KEY[row.payment.product.type]}`)
      : emptyValue;

  const formatDate = (value: string, format: "short" | "time"): string =>
    interpolationFormat(value, format, i18n.language);

  if (error) return <ErrorMessage error={error} />;

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (count === 0) {
    return <EmptyListCard title={t("refunds.list.empty")} />;
  }

  return (
    <Card elevation={0} sx={{ overflowX: "auto" }}>
      <Table size="medium">
        <TableHead>
          <TableRow>
            <TableCell>{t("refunds.list.amountColumn")}</TableCell>
            <TableCell>{t("refunds.list.organizationAmountColumn")}</TableCell>
            <TableCell>{t("refunds.list.providerColumn")}</TableCell>
            <TableCell>{t("refunds.list.buyerColumn")}</TableCell>
            <TableCell>{t("refunds.list.typeColumn")}</TableCell>
            <TableCell sx={{ minWidth: 240 }}>
              {t("refunds.list.productColumn")}
            </TableCell>
            <TableCell>{t("refunds.list.statusColumn")}</TableCell>
            <TableCell align="right" sx={{ width: 160 }}>
              {t("refunds.list.createdAtColumn")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.id} hover>
              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {formatAmount(row)}
                </Typography>
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2" noWrap>
                  {formatOrganizationAmount(row)}
                </Typography>
              </TableCell>

              <TableCell>
                <Typography variant="body2" noWrap>
                  {formatProvider(row)}
                </Typography>
              </TableCell>

              {/* Без email ссылка не рисуется: кликабельное «—» бессмысленно. */}
              <TableCell sx={{ maxWidth: 220 }}>
                <BuyerEmailCell
                  email={row.payment?.buyerEmail}
                  userId={row.payment?.userId}
                  emptyValue={emptyValue}
                />
              </TableCell>

              <TableCell sx={{ whiteSpace: "nowrap" }}>
                <Typography variant="body2">{formatType(row)}</Typography>
              </TableCell>

              <TableCell sx={{ minWidth: 240 }}>
                {row.payment?.product ? (
                  <MuiLink
                    component={RouterLink}
                    color="textPrimary"
                    to={`${PagePaths.products}?tab=edit&id=${row.payment.product.id}`}
                  >
                    {row.payment.product.title}
                  </MuiLink>
                ) : (
                  <Typography variant="body2">{emptyValue}</Typography>
                )}
              </TableCell>

              <TableCell>
                <RefundStatusChip refund={row} />
              </TableCell>

              <TableCell align="right" sx={{ width: 160 }}>
                <Typography variant="body2" color="textPrimary" noWrap>
                  {formatDate(row.createdAt, "short")}
                </Typography>
                <Typography variant="body2" color="textSecondary" noWrap>
                  {formatDate(row.createdAt, "time")}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={onPageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsPerPageChange}
        rowsPerPageOptions={[5, 10, 25, 100]}
        labelRowsPerPage={t("common.pagination.rowsPerPage")}
        labelDisplayedRows={({ from, to, count }) =>
          t("common.pagination.displayedRows", {
            from,
            to,
            count:
              count !== -1 ? count : t("common.pagination.moreThan", { to }),
          })
        }
      />
    </Card>
  );
};
